// Message Scheduler Utility (frontend-only)
// Persists scheduled messages in localStorage and dispatches them when due

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const STORAGE_KEY = 'scheduled_messages_v1';

let dispatchTimerId = null;
const subscribers = new Set();

function loadQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr;
  } catch (e) {
    return [];
  }
}

function saveQueue(queue) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  notifySubscribers(queue);
}

function notifySubscribers(queue = null) {
  const items = queue ?? loadQueue();
  subscribers.forEach((cb) => {
    try { cb(items); } catch (_) {}
  });
  try {
    window.dispatchEvent(new CustomEvent('scheduledMessages:updated', { detail: { items } }));
  } catch (_) {}
}

function generateId() {
  return `sched_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

export function getScheduled() {
  return loadQueue();
}

export function subscribe(callback) {
  subscribers.add(callback);
  // Initial push
  try { callback(loadQueue()); } catch (_) {}
  return () => {
    subscribers.delete(callback);
  };
}

export async function cancelScheduled(id) {
  const queue = loadQueue();
  const item = queue.find((q) => q.id === id || q._backendId === id);
  
  // If this is a backend-synced message, delete from backend
  if (item && (item._backendId || item.source === 'admin:email-mgmt' || item.source === 'admin:sent' || item.source === 'admin:inbox' || item.source === 'admin:user-mgmt')) {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const backendId = item._backendId || item.id;
        await axios.delete(
          `${API_URL}/api/admin/scheduled-messages/${backendId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
    } catch (err) {
      console.warn('Failed to delete scheduled message from backend:', err);
    }
  }
  
  const next = queue.filter((item) => item.id !== id && item._backendId !== id);
  saveQueue(next);
}

export function clearFailed() {
  const queue = loadQueue();
  const next = queue.filter((item) => item.status !== 'failed');
  saveQueue(next);
}

export async function scheduleMessage(options) {
  const {
    recipients, // array of userIds (strings)
    content, // string
    images, // array of base64 encoded images
    scheduledAt, // ISO string or Date-compatible
    source, // e.g. 'admin:inbox', 'user:favorite', etc.
    type = 'direct', // 'direct' | 'reply' | 'batch'
    relatedMessageId = null, // original message id for replies
    markAsReplied = false,
    markAsRead = false,
    needsPointDeduction = false, // for user pages where sending costs points
    senderUserId = null, // for admin sending as specific user
  } = options;

  if (!Array.isArray(recipients) || recipients.length === 0) {
    throw new Error('scheduleMessage: recipients required');
  }
  if (!content || typeof content !== 'string') {
    throw new Error('scheduleMessage: content required');
  }
  const when = new Date(scheduledAt);
  if (Number.isNaN(when.getTime())) {
    throw new Error('scheduleMessage: invalid scheduledAt');
  }

  // If source is admin:email-mgmt, admin:sent, admin:inbox, or admin:user-mgmt, save to backend API
  if (source === 'admin:email-mgmt' || source === 'admin:sent' || source === 'admin:inbox' || source === 'admin:user-mgmt') {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await axios.post(
        `${API_URL}/api/admin/scheduled-messages`,
        {
          recipients,
          content,
          images: images || [],
          scheduledAt: when.toISOString(),
          source,
          type,
          relatedMessageId,
          markAsReplied,
          markAsRead,
          needsPointDeduction,
          senderUserId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Convert backend response to local format for compatibility
      const backendItem = response.data.data;
      const item = {
        id: backendItem._id,
        recipients: backendItem.recipients.map(r => r._id || r),
        content: backendItem.content,
        scheduledAt: backendItem.scheduledAt,
        createdAt: backendItem.createdAt,
        source: backendItem.source,
        type: backendItem.type,
        relatedMessageId: backendItem.relatedMessageId,
        markAsReplied: backendItem.markAsReplied,
        markAsRead: backendItem.markAsRead,
        needsPointDeduction: backendItem.needsPointDeduction,
        senderUserId: backendItem.senderUserId?._id || backendItem.senderUserId,
        status: backendItem.status,
        lastError: backendItem.lastError,
        _backendId: backendItem._id // Keep reference to backend ID
      };

      // Also save to local queue for dispatcher
      const queue = loadQueue();
      queue.push(item);
      saveQueue(queue);
      startDispatcher();
      notifySubscribers();
      return item.id;
    } catch (err) {
      console.error('Failed to save scheduled message to backend:', err);
      throw err;
    }
  }

  // For non-admin sources, use local storage only
  const item = {
    id: generateId(),
    recipients,
    content,
    scheduledAt: when.toISOString(),
    createdAt: new Date().toISOString(),
    source,
    type,
    relatedMessageId,
    markAsReplied,
    markAsRead,
    needsPointDeduction,
    senderUserId,
    status: 'pending', // 'pending' | 'sending' | 'failed'
    lastError: null,
  };

  const queue = loadQueue();
  queue.push(item);
  saveQueue(queue);
  startDispatcher();
  return item.id;
}

async function attemptSend(item) {
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Optional point deduction for user flows
  if (item.needsPointDeduction) {
    try {
      // Check and deduct points at send time
      await axios.post(`${API_URL}/api/users/deduct-points`, {
        actionName: 'send_message',
        quantity: item.recipients.length // Deduct points for all recipients
      }, { headers });
    } catch (err) {
      throw new Error(err?.response?.data?.message || 'ポイント不足または減算に失敗しました');
    }
  }

  // Send to each recipient
  try {
    if (item.senderUserId) {
      // Send as specific user (admin functionality)
      const sendPromises = item.recipients.map((userId) => (
        axios.post(`${API_URL}/api/admin/messages/send-as-user`, {
          fromUserId: item.senderUserId,
          toUserId: userId,
          content: item.content,
        }, { headers })
      ));
      await Promise.all(sendPromises);
    } else {
      // Send as current user (normal functionality)
      const sendPromises = item.recipients.map((userId) => (
        axios.post(`${API_URL}/api/messages/recipientId`, {
          userId,
          content: item.content,
        }, { headers })
      ));
      await Promise.all(sendPromises);
    }
  } catch (err) {
    throw new Error(err?.response?.data?.message || 'メッセージ送信に失敗しました');
  }

  // For reply flows, mark original message flags
  if (item.relatedMessageId && item.markAsReplied) {
    try {
      await axios.put(`${API_URL}/api/messages/${item.relatedMessageId}/reply`, {}, { headers });
    } catch (err) {
      // non-fatal, but record
      // eslint-disable-next-line no-console
      console.warn('Failed to mark as replied:', err);
    }
  }
  if (item.relatedMessageId && item.markAsRead) {
    try {
      await axios.put(`${API_URL}/api/messages/${item.relatedMessageId}/read`, {}, { headers });
    } catch (err) {
      // non-fatal
      // eslint-disable-next-line no-console
      console.warn('Failed to mark as read:', err);
    }
  }
}

function startDispatcher() {
  if (dispatchTimerId) return; // already running

  // Run immediately once
  dispatchDue();
  // And then poll periodically
  dispatchTimerId = window.setInterval(dispatchDue, 5000);
}

async function dispatchDue() {
  const now = Date.now();
  let queue = loadQueue();

  // Pick due items that are still pending
  const dueItems = [];
  for (let i = 0; i < queue.length; i += 1) {
    const it = queue[i];
    if (it.status !== 'pending') continue;
    const dueTime = new Date(it.scheduledAt).getTime();
    if (!Number.isNaN(dueTime) && dueTime <= now) {
      dueItems.push({ ...it, originalIndex: i });
    }
  }

  if (dueItems.length === 0) return;

  // Mark all due items as sending to avoid duplicates
  const updatedQueue = queue.map((it, idx) => {
    const isDue = dueItems.some(dueItem => dueItem.originalIndex === idx);
    return isDue ? { ...it, status: 'sending', lastError: null } : it;
  });
  saveQueue(updatedQueue);

  // Process each due item
  for (const dueItem of dueItems) {
    try {
      // For backend-synced messages (admin:email-mgmt, admin:sent, admin:inbox, admin:user-mgmt), the backend will handle sending
      // We just need to remove them from local queue after backend processes them
      const isBackendSynced = dueItem._backendId || 
        dueItem.source === 'admin:email-mgmt' || 
        dueItem.source === 'admin:sent' || 
        dueItem.source === 'admin:inbox' ||
        dueItem.source === 'admin:user-mgmt';
      
      if (isBackendSynced) {
        // Check backend status - if it's already sent, remove from local queue
        // Don't try to send from frontend, backend handles it
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            // No token available, skip processing (will be handled when user logs in)
            continue;
          }
          
          const backendId = dueItem._backendId || dueItem.id;
          const response = await axios.get(
            `${API_URL}/api/admin/scheduled-messages`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const backendItem = response.data.data?.find(item => item._id === backendId);
          
          if (!backendItem || backendItem.status === 'sent' || backendItem.status === 'failed') {
            // Backend has processed it, remove from local queue
            const currentQueue = loadQueue();
            const filteredQueue = currentQueue.filter((q) => q.id !== dueItem.id);
            saveQueue(filteredQueue);
            notifySubscribers();
            continue;
          }
          
          // Backend item still pending, backend will handle it - just skip frontend sending
          continue;
        } catch (err) {
          // If error is due to no token, silently skip (will be handled when user logs in)
          if (err?.response?.status === 401 || err?.message?.includes('token') || err?.message?.includes('authorization')) {
            continue;
          }
          console.warn('Failed to check backend status:', err);
          // Don't mark as failed for backend-synced messages, just skip
          continue;
        }
      }
      
      // For non-backend-synced messages, attempt to send from frontend
      await attemptSend(dueItem);
      // Remove on success - reload queue to get current state
      const currentQueue = loadQueue();
      const filteredQueue = currentQueue.filter((q) => q.id !== dueItem.id);
      saveQueue(filteredQueue);
      
      notifySubscribers();
    } catch (err) {
      // Mark failed and keep in queue for user to cancel/inspect
      const currentQueue = loadQueue();
      const updatedQueue = currentQueue.map((q) => 
        q.id === dueItem.id ? { ...q, status: 'failed', lastError: String(err?.message || err) } : q
      );
      saveQueue(updatedQueue);
      notifySubscribers();
    }
  }
}

// Load scheduled messages from backend API
export async function loadScheduledFromBackend(source = 'admin:email-mgmt') {
  try {
    const token = localStorage.getItem('token');
    if (!token) return [];

    const response = await axios.get(
      `${API_URL}/api/admin/scheduled-messages?source=${source}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const backendItems = response.data.data || [];
    
    // Filter out sent and failed items, keep only pending
    const pendingItems = backendItems.filter(item => item.status === 'pending');
    
    // Check if any are due and trigger immediate processing
    const now = new Date();
    const dueItems = pendingItems.filter(item => new Date(item.scheduledAt) <= now);
    
    if (dueItems.length > 0) {
      // Backend should handle these, but trigger a check
      console.log(`Found ${dueItems.length} due scheduled message(s), backend will process them`);
    }
    
    // Convert backend format to local format
    const localItems = pendingItems.map(backendItem => ({
      id: backendItem._id,
      recipients: backendItem.recipients.map(r => r._id || r),
      content: backendItem.content,
      scheduledAt: backendItem.scheduledAt,
      createdAt: backendItem.createdAt,
      source: backendItem.source,
      type: backendItem.type,
      relatedMessageId: backendItem.relatedMessageId,
      markAsReplied: backendItem.markAsReplied,
      markAsRead: backendItem.markAsRead,
      needsPointDeduction: backendItem.needsPointDeduction,
      senderUserId: backendItem.senderUserId?._id || backendItem.senderUserId,
      status: backendItem.status,
      lastError: backendItem.lastError,
      _backendId: backendItem._id
    }));

    // Merge with existing local queue (avoid duplicates)
    const existingQueue = loadQueue();
    const existingIds = new Set(existingQueue.map(q => q.id || q._backendId));
    const newItems = localItems.filter(item => !existingIds.has(item.id));
    
    // Remove items from local queue that are sent or no longer in backend
    const backendPendingIds = new Set(pendingItems.map(item => item._id.toString()));
    const backendSentIds = new Set(
      backendItems
        .filter(item => item.status === 'sent')
        .map(item => item._id.toString())
    );
    const cleanedQueue = existingQueue.filter(q => {
      // Check if this is a backend-synced item for the current source
      if (q._backendId || (q.source === source && (q._backendId || q.source === 'admin:email-mgmt' || q.source === 'admin:sent' || q.source === 'admin:inbox' || q.source === 'admin:user-mgmt'))) {
        const backendId = (q._backendId || q.id)?.toString();
        // Remove if sent, keep if still pending
        if (backendSentIds.has(backendId)) {
          return false; // Remove sent items
        }
        return backendPendingIds.has(backendId); // Keep only pending items
      }
      return true; // Keep non-backend items
    });
    
    if (newItems.length > 0 || cleanedQueue.length !== existingQueue.length) {
      const mergedQueue = [...cleanedQueue, ...newItems];
      saveQueue(mergedQueue);
      startDispatcher();
      notifySubscribers();
    }

    return localItems;
  } catch (err) {
    console.error('Failed to load scheduled messages from backend:', err);
    return [];
  }
}

// Auto-start on import in browser contexts
try {
  if (typeof window !== 'undefined') {
    startDispatcher();
    // Also react to storage changes from other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === STORAGE_KEY) {
        notifySubscribers();
      }
    });
  }
} catch (_) {} 